import React from "react";
import * as objectAssignDeep from "../tools/object-assign-deep";
import HcsCaptionControl from "./hcs-captioncontrol";
import HcsNumberInput from "./hcs-number-input";
import HcsInfoButton from "./hcs-info-button";

/**
 * @class
 * @classdesc Number input control with check/uncheck option.
 * The initial state of the control is "unchecked" where the number input
 * field is disabled and does not display any value.
 * When the user then
 *   - clicks on the image or
 *   - enters a number or
 *   - clicks the (+) button
 *   
 * the controls becomes "checked". Then the user can change the quantity number
 * within min/max limits.
 * Another click on the image will "uncheck" the control, and number number value will be removed.
 * 
 * ECS communication is being done as follows:
 *   - check/uncheck: setCfgValue(ident, itemIdent, checked)
 *   - set quantity: setCfgValueQuantity(ident, itemIdent, { quantity: number })
 * @param {object} props - Control properties
 * @param {object} props.hcs - Current HcS instance.
 * @param {function} [props.onConfig] - Call this function when control changes its configuration. This triggers ECS to update its state.
 * Functions parameters:
 *   - method - ECS method like "setCfgValue", "setSubCfgValue", ...
 *   - attrName - In case of "setCfgValue" and "setSubCfgValue": Name of the attribute
 *   - attrValue - In case of "setCfgValue" and "setSubCfgValue": Value of the attribute
 *   - options - Additional options.
 * For more info see the {@link HcsConfigurator#configure} function.
 * @param {object} [props.configWait] - Promise object which waits for a specifiy ECS event like "setCfgValue".
 */
export default class HcsNumberInputCheck extends HcsCaptionControl {
    constructor(props) {
        super(props);

        this.ecsSetValueNumber = this.ecsSetValueNumber.bind(this);
    }

    /**
     * Set "active" state.
     * The new CSS class can be used to highlight the "current" item.
     * @param {any} event
     */
    active(event) {
        let element = event.currentTarget;
        let container = element.parentNode;
        
        let currentSelElement = container.querySelector("li.hcs-sel");

        if(currentSelElement) {
            currentSelElement.classList.remove("hcs-sel");
        }

        element.classList.add("hcs-sel");
    }

    /**
     * Check/uncheck the control.
     * When the user clicks on the image (the first time)
     * the control is being "checked" and the initial value will be
     * set to QTY value which is being delivered by ECS.
     * @param {event} event - JS click event object.
     * @param {object} itemData - ECS item data.
     * @param {function} [success] - Event function being called when state has being changed.
     */
    toggle(itemData, success) {
        let self = this;
        let args = arguments;

        const { hcs, data } = this.props;

        const newState = !itemData.info.selected;

        this.props.onConfig("setCfgValue", data.ident, itemData.ident, {
            sender: "HCS_NUMBER_INPUT_CHECK",
            attrChecked: newState,
            success: (result) => {
                if(result.ecs && result.ecs !== "") {
                    if(args.length > 1) {
                        success(newState);
                    }
                }
            }
        });
    }

    ecsSetValueNumber(itemData, inputField) {
        let self = this;

        const { hcs, data } = this.props;

        let currentValue = itemData.data.QTY ? itemData.data.QTY : 0;
        let attrName = itemData.ident;
        let attrValueNew = parseInt(inputField.value);
        let isChecked = itemData.info.selected && attrValueNew > parseInt(itemData.flags.min);

        let updateQuantity = _ => {
            if(currentValue != attrValueNew) {
                const min = parseInt(itemData.flags.min);
                const max = parseInt(itemData.flags.max);

                if(attrValueNew < min) {
                    attrValueNew = min;
                }

                if(attrValueNew > max) {
                    attrValueNew = max;
                }

                self.props.onConfig("setCfgValueQuantity", data.ident, attrName, {
                    sender: "HCS_NUMBER_INPUT_CHECK",
                    quantity: attrValueNew,
                    success: (result) => {
                        if(result.ecs && result.ecs !== "") {
                            super.triggerConfigurator();
                            self.updating = false;
                        }
                    }
                });
            } else {
                self.updating = false;
            }
        };

        this.props.checkValueChange(this, {
            confirmed: _ => {
                self.updating = true;

                if(!isChecked) {
                    self.toggle(itemData, _ => { updateQuantity(); });
                } else {
                    updateQuantity();
                }
            },
            rejected: _ => {
                inputField.value = currentValue;
            }
        });
    }

    childClass() {
        return "hcs-number-input-check";
    }

    render() {
        const { hcs, data, progressState } = this.props;

        return super.render(
            <ul>
                {
                    data.values.filter(item => item.info.visible).map((itemData, index) => {
                        // Since "HcsNumberInput" reads its min/max/step values from info.max, info.min ....
                        // we convert the "flags" settings to this object.
                        // Also we transfer the "QTY" value to "item.data" so that the input field
                        // shows the correct number.
                        objectAssignDeep(itemData, {
                            info: {
                                minincl: true,
                                maxincl: true,
                                min: parseInt(itemData.flags.min),
                                max: parseInt(itemData.flags.max),
                                step: parseInt(itemData.flags.step),
                                value: itemData.info.selected ? (itemData.data.QTY ? itemData.data.QTY : "0") : "0"
                            }
                        });

                        let itemClasses = [];

                        if(itemData.info.selected) {
                            itemClasses.push("hcs-checked");
                        }

                        return (
                            <li
                                ecs-key={itemData.ident}
                                className={itemClasses.length ? itemClasses.join(" ") : null}
                                key={`hcs-number-input-check-${data.ident}-${index}`}
                                onClick={event => this.active(event)}
                            >
                                <HcsNumberInput
                                    hcs={hcs}
                                    data={itemData}
                                    progressState={progressState}
                                    ecsSetValue={(inputField) => this.ecsSetValueNumber.call(this, itemData, inputField) }
                                    renderOuterListControl={false}
                                >
                                    <div className="hcs-number-input-button">
                                        <a className="hcs-toggle" /*onClick={_ => this.toggle(itemData)}*/>
                                            <img src={hcs.api.getUrl("ConfigImage/icons", itemData.data.IMAGE || "")} />
                                        </a>
                                        {itemData.data.TOOLTIP ?
                                            <HcsInfoButton contentHtml={itemData.data.TOOLTIP} /> : null
                                        }
                                    </div>
                                </HcsNumberInput>
                            </li>
                        );
                    }, this)
                }
            </ul>
        );
    }
}